import React from "react";
import { LoopFieldsSet, FormFieldSet, Button, useFormHandler } from "../index";
import { v4 as uuidv4 } from "uuid";

// Example 1: Education Details Loop
const EducationExample = () => {
  const initialFormData = {
    educationDetails: [
      {
        id: uuidv4(),
        degree: "",
        institution: "",
        year: "",
        grade: "",
      },
    ],
  };

  const { formData, errors, handleChange, setFormData } = useFormHandler(
    initialFormData,
    {
      educationDetails: [
        {
          degree: { type: "text", required: true },
          institution: { type: "text", required: true },
          year: { type: "text", required: true },
          grade: { type: "text", required: false },
        },
      ],
    }
  );

  const addEducation = () => {
    const newEducation = {
      id: uuidv4(),
      degree: "",
      institution: "",
      year: "",
      grade: "",
    };
    setFormData({
      ...formData,
      educationDetails: [...formData.educationDetails, newEducation],
    });
  };

  const removeEducation = (id) => {
    if (formData.educationDetails.length > 1) {
      const updated = formData.educationDetails.filter(
        (item) => item.id !== id
      );
      setFormData({
        ...formData,
        educationDetails: updated,
      });
    }
  };

  return (
    <LoopFieldsSet
      title="Education Details"
      data={formData.educationDetails}
      onAdd={addEducation}
      onRemove={removeEducation}
      minItems={1}
      maxItems={5}
      gridCols="grid-cols-[repeat(4,1fr)_0.4fr]"
      renderFields={(education, index) => (
        <>
          <FormFieldSet
            type="text"
            title="Degree"
            placeholder="Enter Degree"
            value={education.degree || ""}
            onChange={(value) =>
              handleChange(`educationDetails[${index}].degree`, value)
            }
            error={errors.educationDetails?.[index]?.degree}
          />
          <FormFieldSet
            type="text"
            title="Institution"
            placeholder="Enter Institution"
            value={education.institution || ""}
            onChange={(value) =>
              handleChange(`educationDetails[${index}].institution`, value)
            }
            error={errors.educationDetails?.[index]?.institution}
          />
          <FormFieldSet
            type="text"
            title="Year"
            placeholder="Enter Year"
            value={education.year || ""}
            onChange={(value) =>
              handleChange(`educationDetails[${index}].year`, value)
            }
            error={errors.educationDetails?.[index]?.year}
          />
          <FormFieldSet
            type="text"
            title="Grade/CGPA"
            placeholder="Enter Grade"
            value={education.grade || ""}
            onChange={(value) =>
              handleChange(`educationDetails[${index}].grade`, value)
            }
            error={errors.educationDetails?.[index]?.grade}
          />
        </>
      )}
    />
  );
};

// Example 2: Work Experience Loop
const WorkExperienceExample = () => {
  const initialFormData = {
    workExperience: [
      {
        id: uuidv4(),
        company: "",
        position: "",
        duration: "",
      },
    ],
  };

  const { formData, errors, handleChange, setFormData } = useFormHandler(
    initialFormData,
    {
      workExperience: [
        {
          company: { type: "text", required: true },
          position: { type: "text", required: true },
          duration: { type: "text", required: true },
        },
      ],
    }
  );

  const addExperience = () => {
    const newExperience = {
      id: uuidv4(),
      company: "",
      position: "",
      duration: "",
    };
    setFormData({
      ...formData,
      workExperience: [...formData.workExperience, newExperience],
    });
  };

  const removeExperience = (id) => {
    if (formData.workExperience.length > 1) {
      const updated = formData.workExperience.filter((item) => item.id !== id);
      setFormData({
        ...formData,
        workExperience: updated,
      });
    }
  };

  return (
    <LoopFieldsSet
      title="Work Experience"
      data={formData.workExperience}
      onAdd={addExperience}
      onRemove={removeExperience}
      addButtonTitle="Add Experience"
      minItems={1}
      maxItems={8}
      gridCols="grid-cols-[repeat(3,1fr)_0.4fr]"
      renderFields={(experience, index) => (
        <>
          <FormFieldSet
            type="text"
            title="Company"
            placeholder="Enter Company Name"
            value={experience.company || ""}
            onChange={(value) =>
              handleChange(`workExperience[${index}].company`, value)
            }
            error={errors.workExperience?.[index]?.company}
          />
          <FormFieldSet
            type="text"
            title="Position"
            placeholder="Enter Position"
            value={experience.position || ""}
            onChange={(value) =>
              handleChange(`workExperience[${index}].position`, value)
            }
            error={errors.workExperience?.[index]?.position}
          />
          <FormFieldSet
            type="text"
            title="Duration"
            placeholder="Enter Duration"
            value={experience.duration || ""}
            onChange={(value) =>
              handleChange(`workExperience[${index}].duration`, value)
            }
            error={errors.workExperience?.[index]?.duration}
          />
        </>
      )}
    />
  );
};

// Example 3: Custom Buttons with Inline Position
const CustomButtonsExample = () => {
  const initialFormData = {
    skills: [
      {
        id: uuidv4(),
        skillName: "",
        level: "",
      },
    ],
  };

  const { formData, errors, handleChange, setFormData } = useFormHandler(
    initialFormData,
    {
      skills: [
        {
          skillName: { type: "text", required: true },
          level: { type: "select", required: true },
        },
      ],
    }
  );

  const addSkill = () => {
    const newSkill = {
      id: uuidv4(),
      skillName: "",
      level: "",
    };
    setFormData({
      ...formData,
      skills: [...formData.skills, newSkill],
    });
  };

  const removeSkill = (id) => {
    if (formData.skills.length > 1) {
      const updated = formData.skills.filter((item) => item.id !== id);
      setFormData({
        ...formData,
        skills: updated,
      });
    }
  };

  const duplicateSkill = (skill) => {
    const duplicatedSkill = {
      ...skill,
      id: uuidv4(),
    };
    setFormData({
      ...formData,
      skills: [...formData.skills, duplicatedSkill],
    });
  };

  return (
    <LoopFieldsSet
      title="Skills"
      data={formData.skills}
      onAdd={addSkill}
      onRemove={removeSkill}
      minItems={1}
      maxItems={10}
      showBuiltInRemoveButton={false} // Hide built-in remove button
      addRemoveButtonsPosition="both" // Show add button in both header and inline
      gridCols="grid-cols-[repeat(2,1fr)_0.6fr]"
      renderCustomButtons={(position, item, index) => {
        if (position === "inline" && item) {
          return (
            <>
              <Button
                type="icon"
                icon="content_copy"
                variant="text"
                color="primary"
                size="sm"
                onClick={() => duplicateSkill(item)}
                title="Duplicate"
              />
              <Button
                type="icon"
                icon="delete"
                variant="text"
                color="error"
                size="sm"
                onClick={() => removeSkill(item.id)}
                disabled={formData.skills.length <= 1}
                title="Remove"
              />
            </>
          );
        }
        return null;
      }}
      renderFields={(skill, index) => (
        <>
          <FormFieldSet
            type="text"
            title="Skill Name"
            placeholder="Enter Skill Name"
            value={skill.skillName || ""}
            onChange={(value) =>
              handleChange(`skills[${index}].skillName`, value)
            }
            error={errors.skills?.[index]?.skillName}
          />
          <FormFieldSet
            type="select"
            title="Level"
            placeholder="Select Level"
            options={[
              { label: "Beginner", value: "beginner" },
              { label: "Intermediate", value: "intermediate" },
              { label: "Advanced", value: "advanced" },
              { label: "Expert", value: "expert" },
            ]}
            value={skill.level || ""}
            onChange={(value) => handleChange(`skills[${index}].level`, value)}
            error={errors.skills?.[index]?.level}
          />
        </>
      )}
    />
  );
};

export { EducationExample, WorkExperienceExample, CustomButtonsExample };
